/**
 * Rank insignia — one mark per rank, escalating (operator spec: "each looks
 * cooler than the last"). Thin-stroke cyan geometry, console language.
 * Cadet: one chevron · Junior: double chevron · Senior: triple + core dot ·
 * Lead: diamond over bars · Chief: the arc-reactor triangle, ringed, pulsing.
 */
const C = "var(--kid-cyan)";

export default function RankInsignia({ rank, size = 16 }: { rank: string; size?: number }) {
  const common = { fill: "none", stroke: C, strokeWidth: 1.6, strokeLinecap: "round" as const };
  let art: React.ReactNode;
  switch (rank) {
    case "Recrue":
      art = (
        <>
          <polyline points="4,13 12,7 20,13" {...common} />
          <polyline points="4,18 12,12 20,18" {...common} opacity={0.6} />
        </>
      );
      break;
    case "Spécialiste":
      art = (
        <>
          <polyline points="4,10 12,4 20,10" {...common} />
          <polyline points="4,15 12,9 20,15" {...common} opacity={0.75} />
          <polyline points="4,20 12,14 20,20" {...common} opacity={0.5} />
          <circle cx="12" cy="19" r="1.6" fill={C} stroke="none" />
        </>
      );
      break;
    case "Officier":
      art = (
        <>
          <rect x="7.5" y="7.5" width="9" height="9" transform="rotate(45 12 12)" {...common} />
          <line x1="2" y1="20" x2="8" y2="20" {...common} opacity={0.7} />
          <line x1="16" y1="20" x2="22" y2="20" {...common} opacity={0.7} />
          <circle cx="12" cy="12" r="1.6" fill={C} stroke="none" />
        </>
      );
      break;
    case "Lieutenant":
      // Lead's diamond, elevated: winged side-bars + apex tick + brighter core.
      art = (
        <>
          <rect x="7.5" y="7.5" width="9" height="9" transform="rotate(45 12 12)" {...common} />
          <line x1="1" y1="17" x2="7" y2="17" {...common} opacity={0.8} />
          <line x1="17" y1="17" x2="23" y2="17" {...common} opacity={0.8} />
          <line x1="2.5" y1="20" x2="8" y2="20" {...common} opacity={0.5} />
          <line x1="16" y1="20" x2="21.5" y2="20" {...common} opacity={0.5} />
          <line x1="12" y1="1.5" x2="12" y2="4" {...common} opacity={0.7} />
          <circle cx="12" cy="12" r="1.8" fill={C} stroke="none" />
        </>
      );
      break;
    case "Commandant":
      // The reactor housing, not yet lit: static ring + diamond + cardinal ticks.
      // (The pulse stays Chief-exclusive — this one holds its charge.)
      art = (
        <>
          <circle cx="12" cy="12" r="9" {...common} opacity={0.65} />
          <rect x="8" y="8" width="8" height="8" transform="rotate(45 12 12)" {...common} />
          <line x1="12" y1="1" x2="12" y2="3" {...common} opacity={0.6} />
          <line x1="12" y1="21" x2="12" y2="23" {...common} opacity={0.6} />
          <line x1="1" y1="12" x2="3" y2="12" {...common} opacity={0.6} />
          <line x1="21" y1="12" x2="23" y2="12" {...common} opacity={0.6} />
          <circle cx="12" cy="12" r="2" fill={C} stroke="none" />
        </>
      );
      break;
    case "Chef Ingénieur":
      art = (
        <>
          <circle cx="12" cy="12" r="9" {...common} opacity={0.8}>
            <animate attributeName="opacity" values="0.5;0.95;0.5" dur="3s" repeatCount="indefinite" />
          </circle>
          <polygon points="12,6.2 17,15.4 7,15.4" {...common} />
          <circle cx="12" cy="12.6" r="1.8" fill={C} stroke="none">
            <animate attributeName="r" values="1.4;2.2;1.4" dur="2s" repeatCount="indefinite" />
          </circle>
          <line x1="12" y1="1" x2="12" y2="3" {...common} opacity={0.5} />
          <line x1="12" y1="21" x2="12" y2="23" {...common} opacity={0.5} />
          <line x1="1" y1="12" x2="3" y2="12" {...common} opacity={0.5} />
          <line x1="21" y1="12" x2="23" y2="12" {...common} opacity={0.5} />
        </>
      );
      break;
    case "Cadet":
    default:
      art = <polyline points="4,15 12,9 20,15" {...common} opacity={0.85} />;
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {art}
    </svg>
  );
}
